'use client'

import { useCallback, useEffect, useState } from 'react'
import { FormEvoNotificationStatusSelect } from '@repo/ui/FormEvoNotificationStatusSelect'
import { useLookupsStore } from '@repo/store/useLookupsStore'
import MUIButton from '@mui/material/Button'
import MUICard from '@mui/material/Card'
import MUICircularProgress from '@mui/material/CircularProgress'
import MUIFormControlLabel from '@mui/material/FormControlLabel'
import MUIRadio from '@mui/material/Radio'
import MUIRadioGroup from '@mui/material/RadioGroup'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'

export const SettingsPanel = () => {
  const {
    fetchFormEvoNotificationBehaviour,
    fetchFormEvoNotificationBehaviourStatus,
    fetchFormEvoFormUpdateBehaviour,
    fetchFormEvoMatterHistoryUpdateBehavior,
    setFormEvoNotificationBehaviour,
    setFormEvoNotificationBehaviourStatus,
    setFormEvoFormUpdateBehaviour,
    setFormEvoMatterHistoryUpdateBehavior
  } = useLookupsStore()

  const [formEvoNotificationBehaviour, setNotificationBehaviour] = useState('')
  const [formEvoNotificationBehaviourStatus, setNotificationBehaviourStatus] = useState('')
  const [formEvoMatterHistoryUpdateBehavior, setMatterHistoryUpdateBehavior] = useState('')
  const [formEvoFormUpdateBehaviour, setFormUpdateBehaviour] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  const loadSettings = useCallback(async () => {
    try {
      const [notificationBehaviour, notificationBehaviourStatus, matterHistoryUpdateBehavior, formUpdateBehaviour] =
        await Promise.all([
          fetchFormEvoNotificationBehaviour(),
          fetchFormEvoNotificationBehaviourStatus(),
          fetchFormEvoMatterHistoryUpdateBehavior(),
          fetchFormEvoFormUpdateBehaviour()
        ])

      setNotificationBehaviour(notificationBehaviour ?? '')
      setNotificationBehaviourStatus(notificationBehaviourStatus ?? '')
      setMatterHistoryUpdateBehavior(matterHistoryUpdateBehavior ?? '')
      setFormUpdateBehaviour(formUpdateBehaviour ?? '')
    } catch (error) {
      console.error('Error fetching FormEvo settings:', error)
    } finally {
      setIsLoading(false)
    }
  }, [
    fetchFormEvoNotificationBehaviour,
    fetchFormEvoNotificationBehaviourStatus,
    fetchFormEvoMatterHistoryUpdateBehavior,
    fetchFormEvoFormUpdateBehaviour
  ])

  useEffect(() => {
    loadSettings()
  }, [loadSettings])

  const handleCancel = async () => {
    setIsLoading(true)
    await loadSettings()
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await Promise.all([
        setFormEvoNotificationBehaviour({ formEvoNotificationBehaviour }),
        setFormEvoNotificationBehaviourStatus({ formEvoNotificationBehaviourStatus }),
        setFormEvoMatterHistoryUpdateBehavior({ formEvoMatterHistoryUpdateBehavior }),
        setFormEvoFormUpdateBehaviour({ formEvoFormUpdateBehaviour })
      ])
    } catch (error) {
      console.error('Error saving FormEvo settings:', error)
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <MUIStack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
        <MUICircularProgress />
      </MUIStack>
    )
  }

  const statusEnabled = formEvoNotificationBehaviour === 'ImportIntoMatterHistoryOnStatusChange'

  return (
    <MUICard
      data-testid="FormEvoSettingsPanel"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        p: 12,
        maxWidth: 800,
        width: '100%',
        mx: 'auto',
        mt: '3rem',
        backgroundColor: 'common.white'
      }}
    >
      <MUIStack gap={1}>
        <MUITypography variant="body1" sx={{ fontWeight: 600 }}>
          How would you like to import FormEvo PDFs into Matter History?
        </MUITypography>
        <MUIRadioGroup
          value={formEvoNotificationBehaviour}
          onChange={(e) => setNotificationBehaviour(e.target.value)}
        >
          <MUIFormControlLabel value="DoNothing" control={<MUIRadio />} label="Manually" />
          <MUIFormControlLabel
            value="ImportIntoMatterHistory"
            control={<MUIRadio />}
            label="Automatically, whenever a new version is created (e.g. each time a form is updated)"
          />
          <MUIFormControlLabel
            value="ImportIntoMatterHistoryOnStatusChange"
            control={<MUIRadio />}
            label="Automatically, whenever the form changes to a specific status"
          />
        </MUIRadioGroup>

        <div style={{ marginLeft: 32, marginTop: 8 }}>
          <FormEvoNotificationStatusSelect
            value={formEvoNotificationBehaviourStatus}
            onChange={setNotificationBehaviourStatus}
            disabled={!statusEnabled}
          />
        </div>
      </MUIStack>

      <MUIStack gap={1}>
        <MUITypography variant="body1" sx={{ fontWeight: 600 }}>
          When importing FormEvo PDFs into Matter History and an older version of the PDF has already been imported,
          what would you like to happen?
        </MUITypography>
        <MUIRadioGroup
          value={formEvoMatterHistoryUpdateBehavior}
          onChange={(e) => setMatterHistoryUpdateBehavior(e.target.value)}
        >
          <MUIFormControlLabel
            value="AddMatterHistory"
            control={<MUIRadio />}
            label="Add a new Matter History item every time"
          />
          <MUIFormControlLabel
            value="UpdateMatterHistory"
            control={<MUIRadio />}
            label="Update the existing Matter History item with the new version"
          />
          <MUIFormControlLabel value="DoNothing" control={<MUIRadio />} label="Do nothing" />
        </MUIRadioGroup>
      </MUIStack>

      <MUIStack gap={1}>
        <MUITypography variant="body1" sx={{ fontWeight: 600 }}>
          When updating a form and there are data conflicts between Osprey and FormEvo, what would you like to happen by
          default?
        </MUITypography>
        <MUIRadioGroup
          value={formEvoFormUpdateBehaviour}
          onChange={(e) => setFormUpdateBehaviour(e.target.value)}
        >
          <MUIFormControlLabel
            value="OverwriteFormEvoData"
            control={<MUIRadio />}
            label="Overwrite the FormEvo data with Osprey data"
          />
          <MUIFormControlLabel
            value="DoNotOverwriteFormEvoData"
            control={<MUIRadio />}
            label="Leave the FormEvo data as is"
          />
        </MUIRadioGroup>
      </MUIStack>

      <MUIStack direction="row" justifyContent="flex-end" gap={2}>
        <MUIButton variant="text" onClick={handleCancel} disabled={isSaving}>
          Cancel
        </MUIButton>
        <MUIButton variant="contained" color="primary" onClick={handleSave} disabled={isSaving}>
          {isSaving ? <MUICircularProgress size={24} color="inherit" /> : 'Save'}
        </MUIButton>
      </MUIStack>
    </MUICard>
  )
}
